import { TrainFrontTunnel } from 'lucide-react';
import {
  CREW_ROLE_LABELS,
  TRAIN_KINDS,
  railCoOf,
  type RailCoContent,
  type RailCoRules,
  type TrainType,
} from '@london-life/contracts';
import { Input } from '@london-life/ui/components/input';
import { Switch } from '@london-life/ui/components/switch';
import { Textarea } from '@london-life/ui/components/textarea';
import { PageHeader } from '@london-life/ui/admin/page-header';
import { ErrorNotice } from '@london-life/ui/feedback/error-notice';
import { useAdmin } from '../../lib/admin-context';
import { useContent } from './use-content';
import { ContentBar } from './versions';
import { Field, NumberField, type NumberSpec } from './form';

const WEEKDAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

const RULES: NumberSpec<Exclude<keyof RailCoRules, 'weekdays'>>[] = [
  { key: 'registerPence', label: 'Register a company', unit: 'pounds', min: 0, max: 1_000_000_000 },
  { key: 'renamePence', label: 'Rename', unit: 'pounds', min: 0, max: 1_000_000_000 },
  { key: 'repaintPence', label: 'Repaint, per train', unit: 'pounds', min: 0, max: 1_000_000_000 },
  { key: 'maxTrains', label: 'Most trains', unit: 'number', min: 1, max: 50, whole: true },
  { key: 'engineerWagePence', label: 'Engineer, a week', unit: 'pounds', min: 0, max: 100_000_000 },
  { key: 'engineerCovers', label: 'Trains one engineer looks after', unit: 'number', min: 1, max: 20, whole: true },
  { key: 'maxEngineers', label: 'Most engineers', unit: 'number', min: 0, max: 10, whole: true },
  { key: 'levy', label: "Station's levy on takings", unit: 'percent', min: 0, max: 0.5 },
  { key: 'wear', label: 'Wear a day (condition points)', unit: 'number', min: 0, max: 20 },
  { key: 'wearEngineer', label: 'Wear a day with an engineer', unit: 'number', min: 0, max: 20 },
  { key: 'noStart', label: "Won't run below (condition)", unit: 'number', min: 0, max: 60, whole: true },
  { key: 'repairShare', label: 'Breakdown repair (of price)', unit: 'percent', min: 0, max: 0.2 },
  { key: 'excessShare', label: 'Insured excess (of price)', unit: 'percent', min: 0, max: 0.2 },
  { key: 'serviceShare', label: 'Service, per condition point (of price)', unit: 'percent', min: 0, max: 0.01 },
  {
    key: 'allocation',
    label: 'Walk-ins a company shares an evening',
    unit: 'number',
    min: 1,
    max: 100_000,
    whole: true,
  },
  { key: 'takingsCapPence', label: 'Most takings a train, an evening', unit: 'pounds', min: 0, max: 10_000_000_000 },
  { key: 'resaleShare', label: 'Sold back for (of price)', unit: 'percent', min: 0, max: 1 },
  { key: 'resaleWeekly', label: 'Less each week owned (of price)', unit: 'percent', min: 0, max: 0.1 },
  { key: 'resaleFloor', label: 'Never below (of price)', unit: 'percent', min: 0, max: 1 },
  { key: 'fareLow', label: 'Lowest fare (of going rate)', unit: 'percent', min: 0.1, max: 1 },
  { key: 'fareHigh', label: 'Highest fare (of going rate)', unit: 'percent', min: 1, max: 5 },
  { key: 'fareCooldownMinutes', label: 'Minutes between fare changes', unit: 'number', min: 0, max: 1440, whole: true },
  { key: 'catchUp', label: 'Evenings caught up at most', unit: 'number', min: 1, max: 14, whole: true },
];
type TrainNumbers = 'seats' | 'pricePence' | 'times' | 'appeal' | 'energyPence' | 'accessPence' | 'insurePence';
const TRAIN: NumberSpec<TrainNumbers>[] = [
  { key: 'seats', label: 'Seats', unit: 'number', min: 1, max: 2000, whole: true },
  { key: 'pricePence', label: 'Price', unit: 'pounds', min: 0, max: 10_000_000_000 },
  { key: 'times', label: "Times the route's fare", unit: 'number', min: 0.5, max: 5 },
  { key: 'appeal', label: "Riders' liking (0–0.5)", unit: 'number', min: 0, max: 0.5 },
  { key: 'energyPence', label: 'Energy a run', unit: 'pounds', min: 0, max: 100_000_000 },
  { key: 'accessPence', label: 'Track access a week', unit: 'pounds', min: 0, max: 100_000_000 },
  { key: 'insurePence', label: 'Insurance a week', unit: 'pounds', min: 0, max: 100_000_000 },
];

const lines = (text: string, max: number, length: number) =>
  text
    .split('\n')
    .map((item) => item.trim().slice(0, length))
    .filter((item) => item.length >= 2)
    .slice(0, max);

// Rail Co. (as Lagos Life's Bus Co.): what a player's train company costs and earns, the trains they can buy,
// the paint jobs, the crews' names and the names nobody may take. Part of the content draft, published with it.
export function RailCoPage() {
  const { can } = useAdmin();
  const editor = useContent();
  const working = editor.working;
  const readOnly = !can('content.write');
  const railco = railCoOf(working ?? {});
  const change = (fn: (draft: RailCoContent) => void) =>
    editor.edit((content) => {
      content.railco ??= structuredClone(railCoOf(content));
      fn(content.railco);
    });
  const train = (kind: keyof RailCoContent['trains'], fn: (item: TrainType) => void) =>
    change((draft) => fn(draft.trains[kind]));
  return (
    <section>
      <PageHeader
        title="Rail Co."
        description="Players' train companies (as Lagos Life's Bus Co.): the costs and the rules, the trains, the paint jobs and the names. Changes are a draft until they are published."
      />
      <ContentBar editor={editor} />
      {editor.failure && <ErrorNotice error={editor.failure} onRetry={() => void editor.reload()} />}
      {!working && !editor.failure && <p className="admin-muted">Loading…</p>}
      {working && (
        <div className="content-editor">
          <fieldset className="admin-panel content-section" disabled={readOnly} aria-label="The rules">
            <h2 className="content-subtitle">
              <TrainFrontTunnel size={16} aria-hidden="true" /> The rules
            </h2>
            <label className="designer-check">
              <Switch
                checked={railco.visible}
                aria-label="Train companies open"
                onCheckedChange={(on) => change((draft) => void (draft.visible = on))}
              />{' '}
              Players can start train companies
            </label>
            <div className="content-grid">
              {RULES.map((spec) => (
                <NumberField
                  key={spec.key}
                  id={`railco-${spec.key}`}
                  spec={spec}
                  value={railco.rules[spec.key]}
                  onChange={(value) => change((draft) => void (draft.rules[spec.key] = value))}
                />
              ))}
            </div>
            <p className="admin-muted">How busy each evening is (1 is ordinary):</p>
            <div className="content-grid">
              {WEEKDAYS.map((day, index) => (
                <NumberField
                  key={day}
                  id={`railco-day-${index}`}
                  spec={{ key: day, label: day, unit: 'number', min: 0, max: 3 }}
                  value={railco.rules.weekdays[index] ?? 1}
                  onChange={(value) => change((draft) => void (draft.rules.weekdays[index] = value))}
                />
              ))}
            </div>
          </fieldset>

          <fieldset className="admin-panel content-section" disabled={readOnly} aria-label="Trains">
            <h2 className="content-subtitle">Trains</h2>
            <p className="admin-muted">
              Wages are a week. A train runs only with its whole required crew; without the optional one it loses that
              share of riders.
            </p>
            {TRAIN_KINDS.map((kind) => {
              const item = railco.trains[kind];
              return (
                <div className="content-grid content-row" key={kind} data-testid={`railco-train-${kind}`}>
                  <Field id={`railco-${kind}-label`} label="Name">
                    <Input
                      id={`railco-${kind}-label`}
                      value={item.label}
                      maxLength={30}
                      onChange={(e) => train(kind, (entry) => void (entry.label = e.target.value))}
                    />
                  </Field>
                  <Field id={`railco-${kind}-icon`} label="Icon">
                    <Input
                      id={`railco-${kind}-icon`}
                      value={item.icon}
                      maxLength={16}
                      onChange={(e) => train(kind, (entry) => void (entry.icon = e.target.value))}
                    />
                  </Field>
                  {TRAIN.map((spec) => (
                    <NumberField
                      key={spec.key}
                      id={`railco-${kind}-${spec.key}`}
                      spec={spec}
                      value={item[spec.key]}
                      onChange={(value) => train(kind, (entry) => void (entry[spec.key] = value))}
                    />
                  ))}
                  {item.required.map((crew, index) => (
                    <NumberField
                      key={crew.role}
                      id={`railco-${kind}-crew-${crew.role}`}
                      spec={{
                        key: crew.role,
                        label: CREW_ROLE_LABELS[crew.role],
                        unit: 'pounds',
                        min: 0,
                        max: 10_000_000,
                      }}
                      value={crew.wagePence}
                      onChange={(value) => train(kind, (entry) => void (entry.required[index]!.wagePence = value))}
                    />
                  ))}
                  {item.optional && (
                    <>
                      <NumberField
                        id={`railco-${kind}-optional`}
                        spec={{
                          key: 'optional',
                          label: `${CREW_ROLE_LABELS[item.optional.role]} (optional)`,
                          unit: 'pounds',
                          min: 0,
                          max: 10_000_000,
                        }}
                        value={item.optional.wagePence}
                        onChange={(value) => train(kind, (entry) => void (entry.optional!.wagePence = value))}
                      />
                      <NumberField
                        id={`railco-${kind}-penalty`}
                        spec={{ key: 'penalty', label: 'Riders lost without', unit: 'percent', min: 0, max: 0.5 }}
                        value={item.optional.penalty}
                        onChange={(value) => train(kind, (entry) => void (entry.optional!.penalty = value))}
                      />
                    </>
                  )}
                  <Field id={`railco-${kind}-perk`} label="What it's like">
                    <Input
                      id={`railco-${kind}-perk`}
                      value={item.perk}
                      maxLength={80}
                      onChange={(e) => train(kind, (entry) => void (entry.perk = e.target.value))}
                    />
                  </Field>
                </div>
              );
            })}
          </fieldset>

          <fieldset className="admin-panel content-section" disabled={readOnly} aria-label="Paint jobs">
            <h2 className="content-subtitle">Paint jobs</h2>
            {railco.liveries.map((item, index) => (
              <div className="content-grid content-row" key={index} data-testid={`railco-livery-${index}`}>
                <Field id={`railco-livery-${index}-name`} label="Name">
                  <Input
                    id={`railco-livery-${index}-name`}
                    value={item.name}
                    maxLength={20}
                    onChange={(e) => change((draft) => void (draft.liveries[index]!.name = e.target.value))}
                  />
                </Field>
                {(['body', 'stripe'] as const).map((part) => (
                  <Field key={part} id={`railco-livery-${index}-${part}`} label={part === 'body' ? 'Body' : 'Stripe'}>
                    <Input
                      id={`railco-livery-${index}-${part}`}
                      type="color"
                      value={item[part]}
                      onChange={(e) => change((draft) => void (draft.liveries[index]![part] = e.target.value))}
                    />
                  </Field>
                ))}
              </div>
            ))}
          </fieldset>

          <fieldset className="admin-panel content-section" disabled={readOnly} aria-label="Names">
            <h2 className="content-subtitle">Names</h2>
            <div className="content-grid">
              <Field id="railco-engineer" label="The engineer">
                <Input
                  id="railco-engineer"
                  value={railco.engineerName}
                  maxLength={30}
                  onChange={(e) => change((draft) => void (draft.engineerName = e.target.value))}
                />
              </Field>
              <Field id="railco-crew" label="Crew names (one per line)">
                <Textarea
                  id="railco-crew"
                  key={railco.crewNames.join('|')}
                  rows={6}
                  defaultValue={railco.crewNames.join('\n')}
                  onBlur={(e) => {
                    const names = lines(e.target.value, 30, 24);
                    if (names.length) change((draft) => void (draft.crewNames = names));
                  }}
                />
              </Field>
              <Field id="railco-locked" label="Names players can't take (one per line)">
                <Textarea
                  id="railco-locked"
                  key={railco.lockedNames.join('|')}
                  rows={8}
                  defaultValue={railco.lockedNames.join('\n')}
                  onBlur={(e) => change((draft) => void (draft.lockedNames = lines(e.target.value, 60, 40)))}
                />
              </Field>
            </div>
          </fieldset>
        </div>
      )}
    </section>
  );
}
